import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from 'react-bootstrap';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar } from 'components/UI';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import Images from 'assets/images/imageData';
import localization from 'assets/localization/localization';
import { toast } from 'react-toastify';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import {
  getRegularizationFilter,
  getRegularizationRequests,
} from 'services/darwinRecord/darwinRecordApi';
import DarwinRegularizationCard from './components/DarwinRegularizationCard';
import { LIST_STATUS, REGULARIZATION_PAGE_SIZE } from './constants';
import { validateRegularizationListFilters } from './darwinRecordValidation';
import './darwinRecord.css';

const DarwinRegularizationList = ({ history, listStatus, pageTitle, subtitle, mode }) => {
  const { baseUrl } = useContext(BaseUrl);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [filterConfig, setFilterConfig] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [rows, setRows] = useState([]);

  const pageReadyRef = useRef(false);
  const loadMoreRef = useRef(null);
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;

  const appliedFilterLabels = useMemo(() => {
    const labels = [];
    if (appliedFilters.requestor_email) {
      labels.push(`Requestor: ${appliedFilters.requestor_email}`);
    }
    if (appliedFilters.request_type) {
      labels.push(`Type: ${appliedFilters.request_type}`);
    }
    if (appliedFilters.start_date) {
      labels.push(`From: ${appliedFilters.start_date}`);
    }
    if (appliedFilters.end_date) {
      labels.push(`To: ${appliedFilters.end_date}`);
    }
    return labels;
  }, [appliedFilters]);

  const fetchFilterConfig = async () => {
    const { data, error } = await getRegularizationFilter();
    if (error) {
      toast.error(error);
      return;
    }
    setFilterConfig(data || []);
  };

  const fetchList = useCallback(
    async (currentPage, filters) => {
      if (currentPage > 1 && !pageReadyRef.current) return;

      setIsLoading(true);
      const { data, error } = await getRegularizationRequests({
        list_status: listStatus,
        page: currentPage,
        limit: REGULARIZATION_PAGE_SIZE,
        requester: 'MANAGER',
        ...filters,
      });

      if (error) {
        if (currentPage === 1) {
          pageReadyRef.current = false;
          setRows([]);
          setHasMore(false);
        }
        toast.error(error);
      } else {
        const nextRows = data?.results || [];
        setRows((prev) => (currentPage === 1 ? nextRows : [...prev, ...nextRows]));
        setHasMore(nextRows.length >= REGULARIZATION_PAGE_SIZE);
        if (currentPage === 1) {
          pageReadyRef.current = true;
        }
      }
      setIsLoading(false);
    },
    [listStatus]
  );

  const resetAndFetch = useCallback(
    (filters) => {
      pageReadyRef.current = false;
      setPage(1);
      setRows([]);
      setHasMore(false);
      fetchList(1, filters);
    },
    [fetchList]
  );

  useEffect(() => {
    fetchFilterConfig();
    resetAndFetch({});
  }, [resetAndFetch]);

  useEffect(() => {
    if (page > 1 && pageReadyRef.current) {
      fetchList(page, appliedFiltersRef.current);
    }
  }, [page, fetchList]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && pageReadyRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading, rows.length]);

  const filterToggle = () => setShowFilter(!showFilter);

  const applyFilter = (filters) => {
    const validation = validateRegularizationListFilters(filters);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    setAppliedFilters(filters);
    resetAndFetch(filters);
    filterToggle();
  };

  const resetFilters = () => {
    setAppliedFilters({});
    resetAndFetch({});
  };

  const headerBtns = () => (
    <Fragment>
      <div className="darwin-filter-btn-wrap">
        <Button onClick={filterToggle} className="filter-hamburger btn btn-transparent">
          <img src={Images?.Filter} alt="" />
          {localization.showFilters}
        </Button>
        {appliedFilterLabels.length > 0 && <span className="darwin-filter-dot" />}
      </div>
    </Fragment>
  );

  return (
    <Layout title={pageTitle} isLoading={isLoading && rows.length === 0} Buttons={headerBtns}>
      <div className="darwin-record-page">
        {subtitle ? <p className="darwin-subtitle">{subtitle}</p> : null}

        <AppliedFiltersBar
          items={appliedFilterLabels}
          onClear={resetFilters}
          clearButtonLabel="Clear filters"
        />

        {rows.length === 0 && !isLoading ? (
          <div className="darwin-empty-state">
            <p className="darwin-empty-state__title">No requests found</p>
            <p className="darwin-empty-state__subtitle">Try adjusting filters.</p>
          </div>
        ) : (
          <>
            <div className="darwin-record-grid">
              {rows.map((item) => (
                <DarwinRegularizationCard
                  key={`${item.id}-${item.type}`}
                  item={item}
                  listStatus={listStatus}
                  mode={mode}
                  baseUrl={baseUrl}
                  history={history}
                />
              ))}
            </div>
            {hasMore ? <div ref={loadMoreRef} className="darwin-load-more-sentinel" /> : null}
            {isLoading && rows.length > 0 ? (
              <div className="text-center py-3">Loading more...</div>
            ) : null}
          </>
        )}
      </div>

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={applyFilter}
        darwinRegularizationFilter
        darwinRegularizationFilterProps={{
          filterConfig,
          appliedFilters,
        }}
      />
    </Layout>
  );
};

export const DarwinManagerRegularizationList = (props) => (
  <DarwinRegularizationList
    {...props}
    listStatus={LIST_STATUS.PENDING}
    pageTitle="Attendance Regularization Requests"
    mode="action"
  />
);

export const DarwinEmployeeRegularizationList = (props) => (
  <DarwinRegularizationList
    {...props}
    listStatus={LIST_STATUS.APPROVED_REJECTED}
    pageTitle="Regularization Logs"
    mode="view"
  />
);

export default DarwinRegularizationList;
